import type { Nullish } from '@/api/types';

export const normalise = {
  toEmpty: (value: unknown): string => {
    if (value === null || value === undefined || value === 'N/A') return '';
    return String(value).trim();
  },

  forComparison: (value: unknown): string => {
    if (value === null || value === undefined || value === '') return '';
    return String(value).trim().toLowerCase();
  },

  unNumber: (value: unknown): string => {
    if (!value || typeof value !== 'string' || value === 'N/A') return '';
    const cleaned = value
      .replace(/^UN\s*/i, '')
      .trim()
      .split(':')[0];
    return /^\d{4}$/.test(cleaned) ? cleaned : '';
  },

  arrayToString: (value: unknown): string => {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value ? String(value) : '';
  },

  stringToArray: (value: Nullish<string>): string[] => {
    if (!value) return [];
    return value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  },

  /** Validated-side PSN normaliser: keeps words with no lowercase letters (uppercase
   *  names, N.O.S., numeric locants) and drops Hazcheck's lowercase qualifier tail.
   *  C# holds its own copy in ExtractUppercaseCore, for the webhook only. */
  psn: (value: unknown): string => {
    if (!value || typeof value !== 'string') return '';
    return value
      .replace(/[.,;:()%]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length > 0 && w === w.toUpperCase())
      .join(' ')
      .toLowerCase();
  },

  /** Extracted/curated-side PSN normaliser: case-insensitive so curators can type any
   *  casing and still match. */
  psnExtracted: (value: unknown): string => {
    if (!value || typeof value !== 'string') return '';
    return value
      .trim()
      .toLowerCase()
      .replace(/[.,;:()%]/g, ' ')
      .split(/\s+/)
      .filter(Boolean)
      .join(' ');
  },

  /** Normalises packing group — maps "none" to empty. Mirrors backend NormalisePackingGroup. */
  packingGroup: (value: unknown): string => {
    if (value === null || value === undefined || value === '') return '';
    const norm = String(value).trim().toLowerCase();
    return norm === 'none' ? '' : norm;
  },
};

export const validate = {
  isEmpty: (value: unknown): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    return false;
  },
};

export const convert = {
  // Empty → null, never undefined: as an RHF `setValueAs`, an undefined field value is
  // treated as "unset" and gets repopulated from defaultValues when the input remounts
  // (switching cargo/section), silently reverting a field the user deliberately cleared.
  // null is a defined value RHF preserves, and matches the `number | null` wire type.
  toNumber: (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const num = parseFloat(String(value));
    return Number.isFinite(num) ? num : null;
  },

  toString: (value: Nullish<number>): string => {
    if (value === null || value === undefined) return '';
    return String(value);
  },

  toInt: (value: Nullish<string>): number | null => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const num = parseInt(value, 10);
    return isNaN(num) ? null : num;
  },

  nullToEmpty: <T>(value: T | null): T | '' => {
    return value === null ? '' : value;
  },

  emptyToNull: (value: Nullish<string>): string | null => {
    if (!value || typeof value !== 'string') return null;
    return value.trim() === '' ? null : value.trim();
  },
};

// Temperature conversions for flashpoint comparison.
export const temperature = {
  toCelsius: (fahrenheit: number): number => {
    return ((fahrenheit - 32) * 5) / 9;
  },

  toFahrenheit: (celsius: number): number => {
    return (celsius * 9) / 5 + 32;
  },

  normaliseToCelsius: (value: number, unit: string): number => {
    if (unit === 'Fahrenheit') return ((value - 32) * 5) / 9;
    return value;
  },
};
